// TradeJournalPage — Trading journal for daily notes/lessons
// Layout: calendar heatmap, today's entry, recent entries

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfSectionHeaderView, TfTimeRange } from '../../components/TfPrimitives';

interface Entry {
  id: string;
  date: string;
  trades: number;
  pnl: number;
  winRate: number;
  mood: 'great' | 'good' | 'neutral' | 'frustrated' | 'tilted';
  notes: string;
  tags: string[];
}

const ENTRIES: Entry[] = [
  { id: 'e1', date: '2026-07-24', trades: 12, pnl: 184.50, winRate: 0.67, mood: 'great',      notes: 'Clean execution on the NQ breakout. Stayed disciplined and walked away at 11:30 — best decision all week.', tags: ['NQ', 'breakout', 'disciplined'] },
  { id: 'e2', date: '2026-07-23', trades: 8,  pnl: -85.00, winRate: 0.50, mood: 'frustrated', notes: 'Got chopped up in the morning range. Should have waited for the breakout, not anticipated it.', tags: ['ES', 'chop', 'patience'] },
  { id: 'e3', date: '2026-07-22', trades: 15, pnl: 320.00, winRate: 0.73, mood: 'great',      notes: 'Bot killed it today. +$320 on the breakout strategy. Risk was 1%, stayed under max DD.', tags: ['bot', 'breakout', 'NQ'] },
  { id: 'e4', date: '2026-07-19', trades: 5,  pnl: 95.00,  winRate: 0.60, mood: 'good',       notes: 'Half day. Took a quick scalp on CL, called it early. Quality over quantity.', tags: ['CL', 'scalp'] },
  { id: 'e5', date: '2026-07-18', trades: 18, pnl: -240.00, winRate: 0.39, mood: 'tilted',     notes: 'Revenge trading after the 3rd loss. Should have walked away at 10:30. Lesson: max 3 losses a day.', tags: ['tilt', 'revenge', 'lesson'] },
];

const MOOD_META = {
  great:      { color: '#34C759', emoji: '🚀', label: 'Great' },
  good:       { color: '#007AFF', emoji: '👍', label: 'Good' },
  neutral:    { color: '#8E8E93', emoji: '😐', label: 'Neutral' },
  frustrated: { color: '#FF9500', emoji: '😤', label: 'Frustrated' },
  tilted:     { color: '#FF3B30', emoji: '😡', label: 'Tilted' },
};

const RANGE_OPTIONS = ['1W', '1M', '3M', '6M', '1Y'] as const;

export function TradeJournalPage() {
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ mood: 'great' as Entry['mood'], notes: '', tags: '' });
  const [range, setRange] = useState<typeof RANGE_OPTIONS[number]>('1M');

  const totalPnl = ENTRIES.reduce((s, e) => s + e.pnl, 0);
  const tradingDays = ENTRIES.length;
  const avgWin = (ENTRIES.filter((e) => e.pnl > 0).reduce((s, e) => s + e.pnl, 0) / Math.max(1, ENTRIES.filter((e) => e.pnl > 0).length)).toFixed(0);
  const avgLoss = (ENTRIES.filter((e) => e.pnl < 0).reduce((s, e) => s + e.pnl, 0) / Math.max(1, ENTRIES.filter((e) => e.pnl < 0).length)).toFixed(0);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Trading journal</div>
        <button onClick={() => setEditing(true)} data-testid="add-entry" className="px-3 text-[15px] font-semibold text-tf-blue">Add</button>
      </div>

      {/* Hero — week summary */}
      <div className="mx-4 mt-4">
        <TfCard>
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[28px] text-white"
              style={{ background: 'linear-gradient(135deg, #FF9500 0%, #FF3B30 100%)' }}
            >
              📓
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-tf-text-3">This week</div>
              <div className={['text-[24px] font-bold tabular-nums', totalPnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-[12px] bg-tf-surface-1 p-2.5">
              <div className="text-[10px] text-tf-text-3 uppercase">Days</div>
              <div className="text-[16px] font-bold tabular-nums text-tf-text-1">{tradingDays}</div>
            </div>
            <div className="rounded-[12px] bg-tf-surface-1 p-2.5">
              <div className="text-[10px] text-tf-text-3 uppercase">Avg win</div>
              <div className="text-[16px] font-bold tabular-nums text-tf-profit">+${avgWin}</div>
            </div>
            <div className="rounded-[12px] bg-tf-surface-1 p-2.5">
              <div className="text-[10px] text-tf-text-3 uppercase">Avg loss</div>
              <div className="text-[16px] font-bold tabular-nums text-tf-loss">${avgLoss}</div>
            </div>
          </div>
        </TfCard>
      </div>

      {/* Calendar heatmap */}
      <TfSectionHeaderView title="Last 30 days" viewAllLabel="Calendar" onViewAll={() => alert('Open calendar')} />
      <div className="mx-4">
        <TfCard>
          <TfTimeRange value={range} onChange={setRange} options={[...RANGE_OPTIONS]} />
          <div className="grid grid-cols-7 gap-1" data-testid="heatmap">
            {Array.from({ length: 30 }).map((_, i) => {
              const e = ENTRIES.find((x) => x.date === `2026-07-${24 - i}`);
              const color = e ? (e.pnl > 100 ? '#34C759' : e.pnl > 0 ? '#A7E8B4' : e.pnl < -100 ? '#FF3B30' : '#FFB8B5') : 'var(--tf-surface-3, #F0F0F5)';
              return (
                <div
                  key={i}
                  title={e?.date || ''}
                  data-testid={`heatmap-day-${i}`}
                  className={['aspect-square rounded-[6px] flex items-center justify-center text-[10px] font-bold', e ? 'text-white' : 'text-tf-text-3'].join(' ')}
                  style={{ backgroundColor: color }}
                >
                  {24 - i}
                </div>
              );
            })}
          </div>
        </TfCard>
      </div>

      {/* Entries */}
      <TfSectionHeaderView title="Entries" viewAllLabel="Filter" onViewAll={() => alert('Filter entries')} />
      <div className="mx-4 space-y-2">
        {ENTRIES.map((e) => (
          <TfCard key={e.id} className="p-3" testId={`entry-${e.id}`}>
            <div className="flex items-start gap-3">
              <div
                className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-[12px] text-[20px]"
                style={{ backgroundColor: MOOD_META[e.mood].color + '20' }}
              >
                {MOOD_META[e.mood].emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[14px] font-bold text-tf-text-1">{new Date(e.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                    <div className="text-[11px] text-tf-text-3">{e.trades} trades · {(e.winRate * 100).toFixed(0)}% win · {MOOD_META[e.mood].label}</div>
                  </div>
                  <div className={['text-[16px] font-bold tabular-nums', e.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                    {e.pnl >= 0 ? '+' : ''}${Math.abs(e.pnl).toFixed(2)}
                  </div>
                </div>
                <p className="mt-2 text-[13px] text-tf-text-1 leading-relaxed">{e.notes}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {e.tags.map((t) => (
                    <span key={t} className="rounded-full bg-tf-surface-1 px-2 py-0.5 text-[10px] font-medium text-tf-text-3">#{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </TfCard>
        ))}
      </div>

      {/* Add entry sheet */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setEditing(false)}>
          <div
            className="w-full max-w-[420px] rounded-t-[20px] p-4 pb-6 max-h-[90vh] overflow-y-auto text-tf-text-1"
            style={{ background: 'var(--tf-bg)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-tf-border" />
            <h2 className="px-1 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">New journal entry</h2>
            <p className="px-1 pb-3 text-[13px] text-tf-text-3">Today · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>

            <h3 className="px-1 text-[12px] font-semibold uppercase tracking-wider text-tf-text-3 mb-2">How did today feel?</h3>
            <div className="grid grid-cols-5 gap-1.5 px-1">
              {(['great', 'good', 'neutral', 'frustrated', 'tilted'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setDraft({ ...draft, mood: m })}
                  data-testid={`mood-${m}`}
                  className={['flex flex-col items-center gap-1 rounded-[12px] p-2 transition',
                    draft.mood === m ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border active:opacity-40'
                  ].join(' ')}
                >
                  <span className="text-[20px]">{MOOD_META[m].emoji}</span>
                  <span className="text-[10px] font-semibold">{MOOD_META[m].label}</span>
                </button>
              ))}
            </div>

            <h3 className="mt-4 px-1 text-[12px] font-semibold uppercase tracking-wider text-tf-text-3 mb-2">What did you learn?</h3>
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              data-testid="journal-notes"
              placeholder="Wins, losses, what to repeat, what to fix..."
              rows={5}
              className="w-full rounded-[10px] border border-tf-border bg-tf-bg p-3 text-[14px] text-tf-text-1 focus:border-tf-blue focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3"
            />

            <h3 className="mt-3 px-1 text-[12px] font-semibold uppercase tracking-wider text-tf-text-3 mb-2">Tags</h3>
            <input
              type="text"
              value={draft.tags}
              onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              placeholder="NQ, breakout, disciplined..."
              data-testid="journal-tags"
              className="h-10 w-full rounded-[10px] border border-tf-border bg-tf-bg px-3 text-[14px] text-tf-text-1 focus:border-tf-blue focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3"
            />

            <div className="mt-4 flex gap-2">
              <button onClick={() => setEditing(false)} className="h-12 flex-1 rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
              <button onClick={() => { alert('Entry saved!'); setEditing(false); }} data-testid="save-entry" className="h-12 flex-1 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.99]">Save entry</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
